<!-- AddNoteForm.vue -->
<script setup>
import {
  removeTabsAndLineBreaks,
  toTitleCase,
  stripHtml,
} from "@/utils/stringUtils";
import {
  ref,
  inject,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
  computed,
} from "vue";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

import AddTagForm from "../Tag/AddTagForm.vue";
import AddPermissionForm from "../Generics/AddPermissionForm.vue";
import AddTextLimitModal from "../Text/AddTextLimitModal.vue";
import UpgradePage from "@/components/User/UpgradePage.vue";
import TagCardModal from "@/components/Tag/TagCardModal.vue";

import { useTagStore } from "@/stores/useTagStore";
import { useNoteStore } from "@/stores/useNoteStore";
import { useUserStore } from "@/stores/useUserStore";
import { usePermissionStore } from "@/stores/usePermissionStore";
import {
  buildManagerPermissionGrants,
  getDefaultAccessMode,
  getPublicModeHint,
} from "@/utils/permissionDefaults";
import { useGroupStore } from "@/stores/useGroupStore";
import { useTextStore } from "@/stores/useTextStore";

import NoteCustomTextarea from "./NoteCustomTextarea.vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

const lastUsedSource = ref("");
const lastUsedTags = ref([]);
const lastUsedPermissionsMode = ref("public");
const lastUsedCustomPermissions = ref([]);
const lastUsedBody = ref("");

const isBodyExpanded = ref(false);
function handleToggleBodyExpand(expanded) {
  isBodyExpanded.value = !!expanded;
}

const isRightColExpanded = ref(false);
const noteTextareaRef = ref(null);

const showPlanCatalog = ref(false);
const showTextLimitModal = ref(false);
const textLimitInfo = ref({
  limit: null,
  currentUsage: null,
  newUsage: null,
  message: "",
});

const permissionFormKey = ref(0);
const permissionFormRef = ref(null);
const isLoading = ref(true);

const props = defineProps({
  textRangeStart: Number,
  textRangeEnd: Number,
  activityId: Number,
  createdByUserId: Number,
  // ✅ PATCH: indica que o formulário está sendo usado como nota avulsa
  isDetached: {
    type: Boolean,
    default: false,
  },
});

const isModalHidden = inject("isModalHidden");
const selectedText = inject("selectedText");

// 🔹 NOVO: arrastar o modal pelo cabeçalho. O componente fica montado e só
// alterna isModalHidden, então o watch abaixo recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();
const registerMouseUpListener = inject("registerMouseUpListener", () => {});

const tagStore = useTagStore();
const noteStore = useNoteStore();
const userStore = useUserStore();
const permissionStore = usePermissionStore();
const groupStore = useGroupStore();
const textStore = useTextStore();

// ✅ PATCH: nota avulsa não possui coluna de trecho selecionado
const isDetachedNote = computed(() => props.isDetached === true);

// ====================================================================
// CONTEXTO EXIBIDO NA COLUNA DIREITA
// Recorta [rangeStart - 400 .. rangeEnd + 400] do arquivo,
// dividindo em três partes para destacar o trecho selecionado no meio.
// Alteração puramente visual — não afeta payload nem lógica de negócio.
// ====================================================================
const contextDisplay = computed(() => {
  const full = textStore.fileContent;
  if (!full || props.textRangeStart == null || props.textRangeEnd == null) {
    return { before: "", excerpt: selectedText?.value ?? "", after: "" };
  }
  const win = 400;
  const start = Math.max(0, props.textRangeStart - win);
  const end = Math.min(full.length, props.textRangeEnd + win);
  return {
    before: full.slice(start, props.textRangeStart),
    excerpt: full.slice(props.textRangeStart, props.textRangeEnd),
    after: full.slice(props.textRangeEnd, end),
  };
});

const selectedTag = ref([]);
const showAddTagModal = ref(false);
const allTags = ref([]);
const refTitle = ref(null);

const obj = ref({
  noteId: 0,
  activityId: props.activityId,
  tagIds: [],
  title: "",
  body: "",
  source: "",
  textRangeStart: props.textRangeStart,
  textRangeEnd: props.textRangeEnd,
  permissions: [],
});

const showTagCardModal = ref(false);
const selectedTagIdForModal = ref(null);
function openTagCard(tagOption) {
  if (!tagOption?.value) return;
  selectedTagIdForModal.value = tagOption.value;
  showTagCardModal.value = true;
}
function closeTagCard() {
  showTagCardModal.value = false;
  selectedTagIdForModal.value = null;
}

async function handleTagUpdated() {
  await tagStore.fetchTags();
  atualizarSubtipos();
  const selectedIds = selectedTag.value.map((s) => s.value);
  selectedTag.value = selectedIds
    .map((id) => allTags.value.find((s) => s.value === id))
    .filter(Boolean);
}

const loggedUser = computed(() => userStore.currentUser);

function getCreatedByUserName(entity) {
  return (
    entity?.createdByUserName ??
    entity?.CreatedByUserName ??
    entity?.createdByUser?.userName ??
    entity?.CreatedByUser?.UserName ??
    ""
  );
}
function isSysEntity(entity) {
  return (
    (getCreatedByUserName(entity) || "").toString().trim().toLowerCase() ===
    "sys"
  );
}
function buildTagLabel(sub) {
  return (sub?.tagName || "").trim();
}

const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 2000);
}
function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}
function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function handleCloseLimitModal() {
  showTextLimitModal.value = false;
}
function handleOpenPlans() {
  showTextLimitModal.value = false;
  showPlanCatalog.value = true;
}
function handleClosePlanCatalog() {
  showPlanCatalog.value = false;
}

function getTodosGroupId() {
  // Preferência: o "Todos" DO CÍRCULO — grupo mantido pelo sistema com o titular
  // e seus membros ativos. Sem círculo (usuário individual), cai no grupo global
  // da plataforma, como antes.
  const circleDefault = groupStore.groups.find(
    (g) => g.isCircleDefault === true
  );
  if (circleDefault) return circleDefault.groupId ?? null;

  return (
    groupStore.groups.find((g) => g.groupName?.toLowerCase() === "todos")
      ?.groupId ?? null
  );
}
function getPermissionIdByName(name) {
  return (
    permissionStore.permissions.find(
      (p) => p.permissionName?.toLowerCase() === name.toLowerCase()
    )?.permissionId ?? null
  );
}

const creatorUserId = computed(
  () => loggedUser.value?.userId ?? props.createdByUserId ?? null
);
const creatorUserName = computed(() => loggedUser.value?.userName ?? "");

function buildPublicPermissions() {
  const todosId = getTodosGroupId();
  const creatorId = creatorUserId.value;
  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId = getPermissionIdByName("editar");
  if (!creatorId || !visualizarId || !selecionarId || !editarId) {
    console.warn(
      "[AddNoteForm] Não foi possível montar permissões públicas padrão",
      { todosId, creatorId, visualizarId, selecionarId, editarId }
    );
  }
  const perms = [];
  if (todosId && visualizarId && selecionarId) {
    perms.push(
      { entityId: todosId, type: "group", permissionId: visualizarId },
      { entityId: todosId, type: "group", permissionId: selecionarId }
    );
  }
  if (creatorId && visualizarId && selecionarId && editarId) {
    perms.push(
      { entityId: creatorId, type: "user", permissionId: visualizarId },
      { entityId: creatorId, type: "user", permissionId: selecionarId },
      { entityId: creatorId, type: "user", permissionId: editarId }
    );
  }
  // Contexto de membro gerenciado: o titular acompanha o criador em tudo
  // que o membro publica (ver utils/permissionDefaults.js).
  perms.push(
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId })
  );

  return perms;
}

function buildPrivatePermissions() {
  const creatorId = creatorUserId.value;
  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId = getPermissionIdByName("editar");
  if (!creatorId || !visualizarId || !selecionarId || !editarId) return [];
  return [
    { entityId: creatorId, type: "user", permissionId: visualizarId },
    { entityId: creatorId, type: "user", permissionId: selecionarId },
    { entityId: creatorId, type: "user", permissionId: editarId },
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId }),
  ];
}

// Molde com que o formulário abre — regra em utils/permissionDefaults.js
// (getDefaultAccessMode): fechado para quem tem círculo (membro ou titular),
// público só para quem não tem.
function buildInitialPermissions() {
  return getDefaultAccessMode() === "private"
    ? buildPrivatePermissions()
    : buildPublicPermissions();
}

function permsMatch(a, b) {
  if (a.length !== b.length) return false;
  const key = (p) => `${p.type}|${p.entityId}|${p.permissionId}`;
  const setA = new Set(a.map(key));
  return b.every((p) => setA.has(key(p)));
}

const currentAccessMode = computed(() => {
  const perms = obj.value.permissions || [];
  if (perms.length === 0) return "public";
  const publicPerms = buildPublicPermissions();
  if (publicPerms.length > 0 && permsMatch(perms, publicPerms)) return "public";
  const privatePerms = buildPrivatePermissions();
  if (privatePerms.length > 0 && permsMatch(perms, privatePerms))
    return "private";
  return "custom";
});

function selectAccessMode(mode) {
  if (isSubmitting.value) return;
  if (mode === "public") {
    obj.value.permissions = buildPublicPermissions();
  } else if (mode === "private") {
    obj.value.permissions = buildPrivatePermissions();
  } else if (mode === "custom") {
    openPermissionModal();
  }
}

const showPermissionModal = ref(false);
const permissionBackup = ref([]);

// 🔹 NOVO: arrasto próprio do mini-modal, independente do painel principal.
// O v-if recria o DOM a cada abertura, mas o offset vive aqui — daí o reset.
const {
  panelRef: permPanelRef,
  panelStyle: permPanelStyle,
  isDragging: isPermDragging,
  isDraggable: isPermDraggable,
  onHandlePointerDown: onPermHandlePointerDown,
  resetPosition: resetPermPosition,
} = useDraggableModal();

watch(showPermissionModal, (val) => {
  if (val) resetPermPosition();
});

function openPermissionModal() {
  permissionBackup.value = (obj.value.permissions || []).map((p) => ({ ...p }));
  if (
    (!obj.value.permissions || obj.value.permissions.length === 0) &&
    lastUsedCustomPermissions.value.length > 0
  ) {
    obj.value.permissions = [...lastUsedCustomPermissions.value];
  }
  if (!obj.value.permissions || obj.value.permissions.length === 0) {
    obj.value.permissions = buildPublicPermissions();
  }
  showPermissionModal.value = true;
}

function cancelPermissionModal() {
  obj.value.permissions = (permissionBackup.value || []).map((p) => ({ ...p }));
  showPermissionModal.value = false;
}

function confirmPermissionModal() {
  if (
    permissionFormRef.value &&
    typeof permissionFormRef.value.validateBeforeConfirm === "function" &&
    !permissionFormRef.value.validateBeforeConfirm()
  )
    return;
  showPermissionModal.value = false;
}

function atualizarSubtipos() {
  allTags.value = tagStore.tags.map((sub) => ({
    label: buildTagLabel(sub),
    value: sub.tagId,
    isSys: isSysEntity(sub),
  }));
}

const userListForChild = computed(() =>
  (userStore.users || []).map((u) => ({
    id: u.userId,
    name: u.userName,
    photoUrl:
      u.photoUrl ??
      u.photo_url ??
      u.photoPath ??
      u.photo_path ??
      u.userPhotoPath ??
      u.user_photo_path ??
      null,
    photoPath: u.photoPath ?? u.photo_path ?? u.photoUrl ?? u.photo_url ?? null,
    userPhotoPath:
      u.userPhotoPath ??
      u.user_photo_path ??
      u.photoUrl ??
      u.photo_url ??
      u.photoPath ??
      u.photo_path ??
      null,
  }))
);
const groupListForChild = computed(() =>
  (groupStore.groups || []).map((g) => ({
    id: g.groupId,
    name: g.groupName,
    createdByUserId: g.createdByUserId ?? g.CreatedByUserId ?? null,
    createdByUserName: g.createdByUserName ?? g.CreatedByUserName ?? null,
    // Membros do grupo: o AddPermissionForm usa para avisar quando a linha de
    // um usuário substitui a permissão que ele teria pelo grupo (precedência).
    members: g.users ?? g.Users ?? [],
  }))
);
const permissionListForChild = computed(() =>
  (permissionStore.permissions || []).map((p) => ({
    id: p.permissionId,
    name: p.permissionName,
  }))
);

const isSubmitting = ref(false);
const validationErrors = ref({});

function closeModal() {
  isSubmitting.value = false;
  lastUsedSource.value = obj.value.source;
  lastUsedTags.value = [...selectedTag.value];
  const currentMode = currentAccessMode.value;
  lastUsedPermissionsMode.value = currentMode;
  if (currentMode === "custom" && obj.value.permissions?.length) {
    lastUsedCustomPermissions.value = [...obj.value.permissions];
  }
  lastUsedBody.value = obj.value.body;
  // Não mexe no toast aqui — ele é independente (Teleport próprio) e deve
  // continuar visível/some sozinho mesmo depois do modal fechar.
  isModalHidden.value = true;
  registerMouseUpListener();
  obj.value.body = "";
  obj.value.source = "";
  obj.value.permissions = [];
  obj.value.tagIds = [];
  selectedTag.value = [];
  showPermissionModal.value = false;
  isBodyExpanded.value = false;
  permissionFormKey.value++;
}

function recoverLastBody() {
  if (lastUsedBody.value) obj.value.body = lastUsedBody.value;
}

// Restaura o último modo de acesso escolhido. Estava embutido no watch abaixo;
// virou função porque o salvamento rápido (quickSubmit) precisa do mesmo
// comportamento sem que o modal chegue a abrir.
function applyLastUsedPermissions() {
  const mode = lastUsedPermissionsMode.value || getDefaultAccessMode();
  if (mode === "private") {
    obj.value.permissions = buildPrivatePermissions();
    return;
  }
  if (mode === "custom" && lastUsedCustomPermissions.value.length > 0) {
    obj.value.permissions = [...lastUsedCustomPermissions.value];
    return;
  }
  obj.value.permissions = buildPublicPermissions();
}

watch(isModalHidden, async (hidden) => {
  if (hidden === false) {
    // 🔹 NOVO: o modal reabre sem desmontar — recentraliza o painel arrastado
    resetPosition();
    obj.value.source = lastUsedSource.value;
    selectedTag.value = [...lastUsedTags.value];
    applyLastUsedPermissions();
    await nextTick();
    refTitle.value?.focus();
  }
});

watch(selectedTag, (newVal) => {
  obj.value.tagIds = newVal.map((s) => s.value);
});

watch(
  selectedText,
  (newValue) => {
    obj.value.title = removeTabsAndLineBreaks(newValue || "", 100);
    obj.value.body = "";
    obj.value.textRangeStart = props.textRangeStart;
    obj.value.textRangeEnd = props.textRangeEnd;
  },
  { immediate: true }
);

const newTagName = ref("");
function addNewTag(newTag) {
  newTagName.value = toTitleCase(newTag);
  showAddTagModal.value = true;
}

const multiselectTagKey = ref(0);
async function handleTagCreated(created) {
  if (!obj.value.tagIds.includes(created.tagId))
    obj.value.tagIds.push(created.tagId);
  await tagStore.fetchTags();
  atualizarSubtipos();
  const updatedSelection = obj.value.tagIds
    .map((id) => allTags.value.find((s) => s.value === id))
    .filter(Boolean);
  selectedTag.value = updatedSelection;
  showAddTagModal.value = false;
}


async function cadastrar() {
  try {
    if (isSubmitting.value) return;
    const child = permissionFormRef.value;
    if (child && typeof child.validateBeforeConfirm === "function") {
      const isOk = child.validateBeforeConfirm();
      if (!isOk) {
        showPermissionModal.value = true;
        return;
      }
    }
    isSubmitting.value = true;
    validationErrors.value = {};
    obj.value.textRangeStart = props.textRangeStart;
    obj.value.textRangeEnd = props.textRangeEnd;
    if (!obj.value.title || !obj.value.title.trim()) {
      showErrorToast("O campo 'Título' é obrigatório.");
      isSubmitting.value = false;
      return;
    }
    if (currentAccessMode.value === "public") {
      if (!obj.value.permissions || obj.value.permissions.length === 0) {
        obj.value.permissions = buildPublicPermissions();
      }
    }
    const payload = JSON.parse(JSON.stringify(obj.value));
    payload.body = stripHtml(obj.value.body);
    payload.public = currentAccessMode.value === "public";
    await noteStore.createNote(payload, props.activityId);
    // createNote já atualiza a lista paginada (fetchNotesByActivityIdPaged);
    // aqui só resincronizamos allNotes (fonte dos destaques em TextWithNotes.vue).
    await noteStore.getNotesByActivityId(props.activityId, true, true, false, []);

    showSuccessToast("Nota criada com sucesso.");
    // Fecha o modal já — o toast é independente (Teleport próprio) e some
    // sozinho pelo seu timer, sem o modal sobreposto atrás.
    closeModal();
  } catch (error) {
    if (
      error.response?.status === 403 ||
      error.response?.data?.message?.includes("limite")
    ) {
      textLimitInfo.value = {
        limit: error.response?.data?.limit || null,
        currentUsage: error.response?.data?.currentUsage || null,
        newUsage: error.response?.data?.newUsage || null,
        message:
          error.response?.data?.message || "Limite de caracteres atingido",
      };
      showTextLimitModal.value = true;
      isSubmitting.value = false;
      return;
    }
    let userFriendlyMessage = "Ocorreu um erro desconhecido ao criar a nota.";
    if (error.response?.data?.errors) {
      const ve = error.response.data.errors;
      const messages = [];
      for (const field in ve) {
        if (ve[field]?.length > 0) messages.push(ve[field][0]);
      }
      if (messages.length > 0) userFriendlyMessage = messages.join("\n");
    } else if (error.response?.data?.message) {
      userFriendlyMessage = error.response.data.message;
    } else if (error.message) {
      userFriendlyMessage = error.message;
    }
    console.error("Erro detalhado da API:", error.response?.data || error);
    showErrorToast(userFriendlyMessage);
    isSubmitting.value = false;
  }
}

// Salvamento rápido: transforma a seleção em nota sem abrir o modal, só com o
// título. Passa por cadastrar() de propósito — payload, permissões, tratamento
// de erro e o resync de allNotes (que faz o destaque aparecer) ficam num lugar
// só, sem uma segunda implementação para divergir depois.
async function quickSubmit() {
  if (isSubmitting.value || isLoading.value) return false;

  // Título e offsets chegam pelo watch de selectedText e pelas props; esperamos
  // o flush para ler o trecho recém-selecionado, e não o anterior.
  await nextTick();

  const title = removeTabsAndLineBreaks(selectedText?.value || "", 100);
  if (!title.trim()) return false;

  // closeModal() (chamado no fim de cadastrar) sobrescreve a memória do
  // formulário com o estado atual, que aqui é vazio. Guardamos e devolvemos
  // para não apagar fonte, tags e corpo que o usuário deixou no modal.
  const preserved = {
    source: lastUsedSource.value,
    tags: [...lastUsedTags.value],
    body: lastUsedBody.value,
    permissionsMode: lastUsedPermissionsMode.value,
    customPermissions: [...lastUsedCustomPermissions.value],
  };

  obj.value.title = title;
  obj.value.body = "";
  obj.value.source = "";
  obj.value.tagIds = [];
  selectedTag.value = [];
  applyLastUsedPermissions();

  try {
    await cadastrar();
  } finally {
    lastUsedSource.value = preserved.source;
    lastUsedTags.value = preserved.tags;
    lastUsedBody.value = preserved.body;
    lastUsedPermissionsMode.value = preserved.permissionsMode;
    lastUsedCustomPermissions.value = preserved.customPermissions;
  }

  return true;
}

defineExpose({ quickSubmit });

onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      tagStore.fetchTags(),
      permissionStore.fetchAllPermissions(),
      groupStore.fetchGroups(1, 1000),
      userStore.fetchUsers(1, 1000),
    ]);
    atualizarSubtipos();
    obj.value.permissions = buildInitialPermissions();
    if (selectedText && selectedText.value) {
      obj.value.title = removeTabsAndLineBreaks(selectedText.value, 45);
      obj.value.body = selectedText.value;
    } else {
      obj.value.title = "";
      obj.value.body = "";
    }
  } finally {
    isLoading.value = false;
  }
});

onBeforeUnmount(() => {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});
</script>

<template>
  <div v-if="!isModalHidden" class="modal-backdrop" @click.self="!isSubmitting">
    <div
      ref="panelRef"
      class="modal-panel"
      :class="{
 'modal-panel--right-collapsed': !isRightColExpanded,
        'modal-panel--right-expanded': isRightColExpanded,
      }"
      :style="panelStyle"
    >
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">Criar Nota Vinculada</h3>
        <button
          @click="closeModal"
          :disabled="isSubmitting"
          type="button"
          class="modal-close-btn"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form
        id="add-note-form"
        @submit.prevent="cadastrar"
        class="modal-form"
        novalidate
      >
        <div class="modal-body-outer">
          <div
            class="modal-body-inner"
            :class="{
 'modal-body-inner--right-collapsed': !isRightColExpanded,
              'modal-body-inner--right-expanded': isRightColExpanded,
            }"
          >
            <transition name="loading-fade">
              <div
                v-if="isLoading"
                class="loading-overlay loading-overlay--translucent"
                aria-label="Carregando..."
              >
                <svg class="loading-spinner" viewBox="0 0 24 24" fill="none">
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
                <span class="loading-text">Carregando...</span>
              </div>
            </transition>

            <div v-if="!isLoading" class="note-two-cols">
              <!-- ── Coluna esquerda ── -->
              <div
                class="note-col-left divider-line"
                :class="{ 'note-col-left-expanded': isBodyExpanded }"
              >
                <div class="step-content note-step-top">
                  <div class="form-group" v-show="!isBodyExpanded">
                    <label for="subtype" class="form-label"
                      >Tag (opcional)</label
                    >
                    <Multiselect
                      v-model="selectedTag"
                      :options="allTags"
                      :multiple="true"
                      placeholder="Selecione ou crie tags para a nota"
                      label="label"
                      track-by="value"
                      :taggable="true"
                      tag-placeholder="Tecle enter ou clique aqui para criar uma tag"
                      select-label="Tecle enter para selecionar"
                      deselect-label="Tecle enter para remover"
                      selected-label="Selecionado"
                      :allow-empty="true"
                      @tag="addNewTag"
                      :disabled="isSubmitting"
                      :key="multiselectTagKey"
                    >
                      <template #option="{ option }">
                        <div class="ms-option-row">
                          <span class="ms-option-text">{{ option.label }}</span>
                          <span v-if="option.isSys" class="sys-badge">SYS</span>
                        </div>
                      </template>
                      <template #tag="{ option, remove }">
                        <span class="ms-chip">
                          <button
                            type="button"
                            class="ms-chip-link"
                            @click.stop="openTagCard(option)"
                          >
                            {{ option.label }}
                          </button>
                          <span v-if="option.isSys" class="sys-badge">SYS</span>
                          <button
                            type="button"
                            class="ms-chip-remove"
                            @click.stop="remove(option)"
                            aria-label="Remover"
                          >
                            ×
                          </button>
                        </span>
                      </template>
                    </Multiselect>
                  </div>

                  <div class="form-group" v-show="!isBodyExpanded">
                    <label for="title" class="form-label">Título</label>
                    <input
                      ref="refTitle"
                      v-model="obj.title"
                      type="text"
                      id="title"
                      class="input-base"
                      placeholder="Digite o título da nota"
                      required
                      :disabled="isSubmitting"
                    />
                  </div>

                  <div class="form-group" v-show="!isBodyExpanded">
                    <label for="source" class="form-label"
                      >Fonte (opcional)</label
                    >
                    <input
                      v-model="obj.source"
                      type="text"
                      id="source"
                      class="input-base"
                      placeholder="Digite a fonte da nota"
                      :disabled="isSubmitting"
                    />
                  </div>

                  <div
                    class="form-group"
                    :class="[isBodyExpanded ? 'body-expanded' : '']"
                  >
                    <div class="body-label-row">
                      <label for="body" class="form-label"
                        >Corpo (opcional)</label
                      >
                      <button
                        type="button"
                        class="ct-expand-btn-inline"
                        :disabled="isSubmitting"
                        @click="noteTextareaRef?.toggleExpand()"
                        :aria-label="
                          isBodyExpanded
                            ? 'Colapsar corpo da nota'
                            : 'Expandir corpo da nota'
                        "
                        :title="
                          isBodyExpanded
                            ? 'Colapsar corpo da nota'
                            : 'Expandir corpo da nota'
                        "
                      >
                        <i
                          v-if="!isBodyExpanded"
                          class="fa fa-expand"
                          aria-hidden="true"
                        ></i>
                        <i v-else class="fa fa-compress" aria-hidden="true"></i>
                      </button>
                      <button
                        v-if="lastUsedBody"
                        type="button"
                        @click="recoverLastBody"
                        class="recover-last-body-btn"
                        title="Usar o último conteúdo preenchido"
                      >
                        Preencher último
                      </button>
                    </div>
                    <NoteCustomTextarea
                      ref="noteTextareaRef"
                      v-model:body="obj.body"
                      :disabled="isSubmitting"
                      @toggle-expand="handleToggleBodyExpand"
                    />

                    <p class="body-hint" v-show="!isBodyExpanded">
                      <i class="fas fa-lightbulb" aria-hidden="true"></i>
                      <span>
                        Cole o link de uma imagem, documento ou site para
                        exibi-lo no corpo. Use <code>[rótulo]</code> antes do
                        link para dar um nome a ele. Link entre aspas é
                        tratado como texto.
                      </span>
                    </p>
                  </div>

                  <div class="form-group" v-show="!isBodyExpanded">
                    <label class="form-label">Quem pode ver esta nota?</label>
                    <div class="access-segment access-segment--sm">
                      <button
                        type="button"
                        class="segment-btn"
                        :class="{
 'segment-btn--active': currentAccessMode === 'public',
                          'segment-btn--public': currentAccessMode === 'public',
                        }"
                        :disabled="isSubmitting"
                        @click="selectAccessMode('public')"
                      >
                        <i class="fas fa-globe-americas segment-btn__icon"></i>
                        <span class="segment-btn__label">Público</span>
                      </button>
                      <button
                        type="button"
                        class="segment-btn"
                        :class="{
 'segment-btn--active':
                            currentAccessMode === 'private',
                          'segment-btn--private':
                            currentAccessMode === 'private',
                        }"
                        :disabled="isSubmitting"
                        @click="selectAccessMode('private')"
                      >
                        <i class="fas fa-lock segment-btn__icon"></i>
                        <span class="segment-btn__label">Privado</span>
                      </button>
                      <button
                        type="button"
                        class="segment-btn"
                        :class="{
 'segment-btn--active': currentAccessMode === 'custom',
                          'segment-btn--custom': currentAccessMode === 'custom',
                        }"
                        :disabled="isSubmitting"
                        @click="selectAccessMode('custom')"
                      >
                        <i class="fas fa-user-shield segment-btn__icon"></i>
                        <span class="segment-btn__label">Personalizado</span>
                      </button>
                    </div>
                    <p class="access-hint">
                      <template v-if="currentAccessMode === 'public'">
                        <i class="fas fa-info-circle"></i> {{ getPublicModeHint() }}
                        poderão visualizar esta nota.
                      </template>
                      <template v-else-if="currentAccessMode === 'private'">
                        <i class="fas fa-info-circle"></i> Somente você poderá
                        acessar esta nota.
                      </template>
                      <template v-else>
                        <i class="fas fa-info-circle"></i> Acesso restrito às
                        pessoas configuradas.
                        <button
                          type="button"
                          class="access-hint__link"
                          @click="openPermissionModal"
                          :disabled="isSubmitting"
                        >
                          Editar permissões
                        </button>
                      </template>
                    </p>
                  </div>

                  <div style="flex-grow: 1" v-show="!isBodyExpanded"></div>
                </div>
              </div>

              <!-- ── wrapper da coluna direita ── -->
              <div
                v-if="!isDetachedNote"
                class="note-col-right-wrapper"
                :class="{
 'note-col-right-wrapper--collapsed': !isRightColExpanded,
                }"
              >
                <div class="note-col-right">
                  <div class="step-content">
                    <div class="form-group">
                      <label for="selectedText" class="form-label">
                        Texto selecionado
                      </label>
                      <!-- Exibe trecho com contexto de ±400 chars; alteração apenas visual -->
                      <div
                        class="context-display whitespace-pre-wrap break-words font-mono"
                      >
                        <span class="context-before">{{
                          contextDisplay.before
                        }}</span>
                        <mark class="context-excerpt">{{
                          contextDisplay.excerpt
                        }}</mark>
                        <span class="context-after">{{
                          contextDisplay.after
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            v-if="!isLoading && !isDetachedNote"
            type="button"
            class="col-right-toggle-btn"
            :class="{ 'col-right-toggle-btn--expanded': isRightColExpanded }"
            @click="isRightColExpanded = !isRightColExpanded"
            :aria-label="
              isRightColExpanded ? 'Colapsar trecho' : 'Expandir trecho'
            "
          >
            <i
              :class="
 isRightColExpanded
                  ? 'fas fa-chevron-left'
                  : 'fas fa-chevron-right'
              "
            ></i>
          </button>
        </div>

        <div class="modal-footer">
          <button
            type="submit"
            form="add-note-form"
            class="btn btn-success"
            :disabled="isSubmitting || isLoading"
          >
            <svg
              v-if="isSubmitting"
              class="mr-1 -ml-1 w-5 h-5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <svg
              v-else
              class="mr-1 -ml-1 w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clip-rule="evenodd"
              ></path>
            </svg>
            <span>{{ isSubmitting ? "Criando..." : "Criar nova nota" }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <TagCardModal
    :show="showTagCardModal"
    :tagId="selectedTagIdForModal"
    @close="closeTagCard"
    @tag-updated="handleTagUpdated"
  />

  <Teleport to="body">
    <transition name="permission-modal-fade">
      <div v-if="showPermissionModal" class="permission-modal-backdrop">
        <div
          ref="permPanelRef"
          class="permission-modal-panel"
          :style="permPanelStyle"
          @click.stop
        >
          <div
            class="permission-modal-header"
            :class="{
 'permission-modal-header--draggable': isPermDraggable,
              'permission-modal-header--dragging': isPermDragging,
            }"
            @pointerdown="onPermHandlePointerDown"
          >
            <h4 class="permission-modal-title">Editar Permissões</h4>
            <button
              type="button"
              class="permission-modal-close"
              @click="cancelPermissionModal"
              aria-label="Fechar Editar Permissões"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                ></path>
              </svg>
            </button>
          </div>
          <div class="permission-modal-body">
            <p class="permission-modal-instruction">
              Defina aqui usuários, grupos e permissões personalizadas para esta
              nota.
            </p>
            <AddPermissionForm
              ref="permissionFormRef"
              v-model:payload="obj.permissions"
              :key="permissionFormKey"
              :users="userListForChild"
              :groups="groupListForChild"
              :permissions="permissionListForChild"
              entityType="Note"
              :creatorUserName="creatorUserName"
            />
          </div>
          <div class="permission-modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="cancelPermissionModal"
              :disabled="isSubmitting"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-success"
              @click="confirmPermissionModal"
              :disabled="isSubmitting"
            >
              Confirmar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>

  <Teleport to="body">
    <div
      v-if="showAddTagModal"
      class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/30 p-4"
      @click.self="showAddTagModal = false"
    >
      <div class="w-full max-w-2xl" @click.stop>
        <AddTagForm
          :initialName="newTagName"
          @tag-added="handleTagCreated"
          @close="showAddTagModal = false"
        />
      </div>
    </div>

    <AddTextLimitModal
      :show="showTextLimitModal"
      :info="textLimitInfo"
      entityType="notas"
      @close="handleCloseLimitModal"
      @open-plans="handleOpenPlans"
    />

    <!-- Mesmo fluxo de upgrade do DashboardLayout (catálogo + checkout);
         z-index acima do formulário de nota e do modal de limite -->
    <UpgradePage
      v-if="showPlanCatalog"
      :initial-step="2"
      :redirect-on-catalog-close="false"
      :z-index="10010"
      @close-upgrade-modal="handleClosePlanCatalog"
      @close="handleClosePlanCatalog"
    />

    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>

.modal-panel {
  width: auto;
  min-width: 580px;
  max-width: 96rem;
  min-height: 700px;
  max-height: calc(100vh - 20px - 2rem);
}

.modal-panel--right-collapsed {
  width: calc(550px + 2.5rem);
  min-width: 0;
  overflow: visible;
}

.modal-panel--right-expanded {
  width: calc(550px + 550px + 2rem);
  min-width: 0;
  overflow: hidden;
}

.modal-form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.modal-body-outer {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-content);
}

.modal-body-inner {
  flex: 1 1 auto;
  min-height: 0;
  padding: 1rem 1rem 1rem 1rem;
  background-color: var(--bg-content);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.modal-body-inner--right-collapsed {
  padding: 1rem;
  overflow: visible;
}

.modal-body-inner--right-collapsed .note-col-left {
  padding-right: 0;
}

.modal-body-inner--right-expanded {
  padding-right: 1rem;
  overflow: hidden;
}

.modal-footer {
  bottom: 0;
}


.note-two-cols {
  overflow: hidden;
  display: flex;
  align-items: stretch;
  gap: 0;
  min-height: 0;
  flex: 1 1 auto;
}

.note-col-left {
  width: 550px;
  flex: 0 0 550px;
  min-height: 0;
  overflow-y: auto;
  padding-right: 1.5rem;
}

.col-right-toggle-btn {
  position: absolute;
  right: -14px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid var(--border-color);
  background: var(--bg-content);
  color: var(--text-light);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.72rem;
  z-index: 20;
  transition: background-color 0.15s, right 0.3s ease;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}
.col-right-toggle-btn--expanded {
  right: calc(550px + 1rem - 14px);
}
.col-right-toggle-btn:hover {
  background: var(--neutral-100);
}

.note-col-right-wrapper {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
  overflow: hidden;
  transition: width 0.3s ease;
  width: 550px;
}
.note-col-right-wrapper--collapsed {
  width: 0;
}

.note-step-top {
  margin-top: -0.25rem;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
}

.form-label {
  flex-shrink: 0;
}
/* Dica de rotulação/preview de links no campo Corpo */
.body-hint {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  margin: 0.4rem 0 0;
  font-size: 0.72rem;
  line-height: 1.4;
  color: var(--text-muted);
}
.body-hint i {
  margin-top: 0.15rem;
  font-size: 0.72rem;
  color: var(--warning);
  flex-shrink: 0;
}
.body-hint code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.7rem;
  padding: 0.05rem 0.3rem;
  border-radius: 4px;
  background-color: var(--surface-3);
  color: #4338ca;
}

.divider-line {
  position: relative;
}

.note-col-right {
  width: 550px;
  flex-shrink: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding-left: 1.5rem;
  box-sizing: border-box;
}
.note-col-right .step-content {
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
}
.note-col-right .form-group {
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
}
/* Substitui o antigo .themed-blockquote como elemento que ocupa o espaço flex */
.note-col-right .context-display {
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
}

/* ── Context display ─────────────────────────────────────────────── */
.context-display {
  background-color: var(--bg-header);
  border-left: 4px solid var(--brand-light);
  border-radius: 8px;
  padding: 1rem;
  color: var(--text-dark);
  overflow-y: auto;
  box-sizing: border-box;
  font-size: 0.95rem;
  white-space: pre-wrap;
  word-break: break-word;
}
.context-before,
.context-after {
  color: var(--text-muted);
}
/* Mesmo highlight do AddAiNoteForm, mas com border-radius mais arredondado */
.context-excerpt {
  background-color: transparent;
  color: #0f172a;
  border-radius: 8px;
  padding: 1px 4px;
  font-weight: 600;
}

.body-label-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ct-expand-btn-inline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 4px;
  border: none;
  background: var(--neutral-200);
  color: var(--text-dark);
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.18s ease-in-out;
  flex-shrink: 0;
}
.ct-expand-btn-inline:hover:not(:disabled) {
  background: var(--neutral-400);
  transform: translateY(-1px);
}
.ct-expand-btn-inline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.recover-last-body-btn {
  margin-left: auto;
  background: none;
  border: none;
  padding: 0;
  font-size: 0.69rem;
  font-weight: 600;
  color: #4f46e5;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: color 0.15s;
}
.recover-last-body-btn:hover {
  color: #3730a3;
}

.access-segment {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.segment-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  padding: 0.6rem 0.5rem;
  border-radius: 12px;
  border: 1.5px solid var(--neutral-200);
  background-color: var(--surface-2);
  cursor: pointer;
  transition: all 0.18s ease;
  color: var(--text-muted);
}
.access-segment--sm .segment-btn {
  padding: 0.45rem 0.35rem;
  border-radius: 10px;
}
.segment-btn:hover:not(:disabled):not(.segment-btn--active) {
  border-color: var(--neutral-400);
  background-color: var(--surface-3);
  color: var(--text-dark);
  transform: translateY(-1px);
}
.segment-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.segment-btn__icon {
  font-size: 1rem;
  line-height: 1;
}
.access-segment--sm .segment-btn__icon {
  font-size: 0.85rem;
}
.segment-btn__label {
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1;
}
.access-segment--sm .segment-btn__label {
  font-size: 0.72rem;
}
.segment-btn--public.segment-btn--active {
  border-color: var(--success);
  background-color: var(--success-bg);
  color: var(--success-text);
  box-shadow: 0 2px 8px rgba(22, 163, 74, 0.12);
}
.segment-btn--private.segment-btn--active {
  border-color: var(--warning);
  background-color: var(--warning-bg);
  color: var(--warning-text);
  box-shadow: 0 2px 8px rgba(217, 119, 6, 0.12);
}
.segment-btn--custom.segment-btn--active {
  border-color: var(--brand);
  background-color: var(--brand-tint);
  color: var(--text-brand);
  box-shadow: 0 2px 8px rgba(155, 132, 255, 0.15);
}

.access-hint {
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.35rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-wrap: wrap;
  line-height: 1.4;
}
.access-hint i {
  font-size: 0.72rem;
  flex-shrink: 0;
}
.access-hint__link {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--brand);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.access-hint__link:hover:not(:disabled) {
  color: #7c63f5;
}
.access-hint__link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ms-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 7px;
  border: 0px solid var(--neutral-400);
  background: var(--surface-3);
  font-size: 0.85rem;
  margin: 2px 6px 2px 0;
}
.ms-chip-remove {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0 2px;
  color: var(--text-muted);
}
.ms-chip-remove:hover {
  color: #0f172a;
}
.ms-chip-link {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: inherit;
  font: inherit;
  line-height: 1.1;
  text-decoration: none;
}
.ms-chip-link:hover {
  color: var(--link);
  text-decoration: underline;
}
.ms-chip-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25);
  border-radius: 6px;
}
.ms-option-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.ms-option-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.sys-badge) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  padding: 0 6px;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(226, 232, 240, 0.8);
  color: rgba(51, 65, 85, 0.95);
  flex-shrink: 0;
  line-height: 1;
  white-space: nowrap;
}

.body-expanded {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.body-expanded :deep(.ct-wrapper) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.body-expanded :deep(.ct-textarea) {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}
.note-col-left-expanded {
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
}
.note-col-left-expanded .step-content {
  flex: 1 1 auto;
  min-height: 0;
}

.permission-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10100;
  background-color: rgba(15, 23, 42, 0.2);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 50px;
  padding-left: 1rem;
  padding-right: 1rem;
}
.permission-modal-panel {
  width: 590px;
  max-width: 100%;
  height: 760px;
  max-height: calc(100vh - 100px);
  background-color: var(--bg-content);
  border-radius: 18px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-modal-lg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.permission-modal-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-header);
}
/* 🔹 NOVO: cabeçalho do mini-modal como alça de arrasto.
   touch-action: none entrega o gesto ao pointermove em vez da rolagem. */
.permission-modal-header--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.permission-modal-header--dragging {
  cursor: grabbing;
}
/* Os botões do cabeçalho continuam sendo clique, não alça */
.permission-modal-header--draggable button {
  cursor: pointer;
}

.permission-modal-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}
.permission-modal-close {
  color: var(--text-light);
  background-color: transparent;
  border-radius: 8px;
  padding: 0.375rem;
  transition: background-color 0.2s, color 0.2s;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}
.permission-modal-close:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: var(--text-dark);
}
.permission-modal-close svg {
  width: 1.25rem;
  height: 1.25rem;
}
.permission-modal-body {
  padding: 0.8rem 1rem 0.5rem;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-content);
  min-height: 0;
  overflow: hidden;
}
.permission-modal-instruction {
  font-size: 0.82rem;
  color: var(--text-light);
  margin-bottom: 0.75rem;
  line-height: 1.35;
  display: block;
}
.permission-modal-footer {
  flex: 0 0 auto;
  padding: 0.6rem 1rem 0.75rem;
  border-top: 1px solid var(--border-color);
  background-color: var(--bg-header);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
}
:deep(.permission-form-wrapper) {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
:deep(.mt-5) {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 1rem !important;
}
:deep(.table-wrap) {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: auto;
  border: 0px solid var(--neutral-200);
  border-radius: 8px;
  background: var(--bg-content);
  margin-top: 0.5rem;
}
:deep(.table-wrap::-webkit-scrollbar) {
  width: 6px;
}
:deep(.table-wrap::-webkit-scrollbar-thumb) {
  background-color: var(--neutral-400);
  border-radius: 10px;
}
.permission-modal-fade-enter-active,
.permission-modal-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.18s ease;
}
.permission-modal-fade-enter-from,
.permission-modal-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (max-width: 680px) {
  .modal-panel--right-collapsed {
    width: min(calc(550px + 2.5rem), calc(100vw - 2rem));
  }
  .modal-body-inner--right-collapsed {
    padding: 1rem;
  }
  .note-col-left {
    width: 100%;
    flex-basis: 100%;
  }
  .modal-body-inner--right-collapsed .note-col-left {
    padding-right: 0;
  }
}

:deep(.ct-textarea) {
  min-height: 120px !important;
  height: 120px !important;
}
</style>
